/**
 * Client-side mirror of server's EMPLOYEE_FIELD_SECTIONS
 * from src/server/middleware/portal/field-metadata.ts
 *
 * Used by the RoleEditor to render the hierarchical field permission grid.
 */

export interface ClientFieldSection {
    key: string
    label: { et: string; en: string }
    fields: string[]
}

export const EMPLOYEE_FIELD_SECTIONS: ClientFieldSection[] = [
    {
        key: 'basic_information',
        label: { et: 'Põhiandmed', en: 'Basic Information' },
        fields: [
            'first_name',
            'last_name',
            'profile_picture',
            'work_email',
            'personal_email',
            'work_phone',
            'personal_phone',
            'home_address',
            'birth_date',
            'nationalities',
            'spoken_languages',
            'gender',
            'fun_facts',
            'personal_id_code',
        ],
    },
    {
        key: 'health',
        label: { et: 'Tervis', en: 'Health' },
        fields: ['dietary_preference', 'allergies', 'prefer_not_to_eat'],
    },
    {
        key: 'banking',
        label: { et: 'Pangainfo', en: 'Banking' },
        fields: ['bank_name', 'bank_account_holder', 'iban', 'swift'],
    },
    {
        key: 'emergency_contacts',
        label: { et: 'Hädaabikontaktid', en: 'Emergency Contacts' },
        fields: ['emergency_contacts'],
    },
    {
        key: 'experiences',
        label: { et: 'Kogemused', en: 'Experiences' },
        fields: ['experiences'],
    },
    {
        key: 'employment',
        label: { et: 'Töösuhe', en: 'Employment' },
        fields: [
            'employment_start_date',
            'probation_end_date',
            'employee_id',
            'contract_type',
            'place_of_work',
            'teams',
            'contract_date',
            'job_title',
            'role_details',
            'working_hours_per_week',
        ],
    },
    {
        key: 'compensation',
        label: { et: 'Tasustamine', en: 'Compensation' },
        fields: ['salary', 'bonus', 'sport_benefit', 'health_benefit'],
    },
    {
        key: 'offboarding',
        label: { et: 'Lahkumine', en: 'Offboarding' },
        fields: [
            'employment_end_date',
            'leaving_reason',
            'leaving_file',
            'leaving_note',
            'turnover_impact',
        ],
    },
]

export const EMPLOYEE_FIELD_LABELS: Record<string, { et: string; en: string }> = {
    first_name: { et: 'Eesnimi', en: 'First name' },
    last_name: { et: 'Perenimi', en: 'Last name' },
    profile_picture: { et: 'Profiilipilt', en: 'Profile picture' },
    work_email: { et: 'Töö e-post', en: 'Work email' },
    personal_email: { et: 'Isiklik e-post', en: 'Personal email' },
    work_phone: { et: 'Töötelefon', en: 'Work phone' },
    personal_phone: { et: 'Isiklik telefon', en: 'Personal phone' },
    home_address: { et: 'Kodune aadress', en: 'Home address' },
    birth_date: { et: 'Sünnipäev', en: 'Birth date' },
    nationalities: { et: 'Kodakondsused', en: 'Nationalities' },
    spoken_languages: { et: 'Räägitavad keeled', en: 'Spoken languages' },
    gender: { et: 'Sugu', en: 'Gender' },
    fun_facts: { et: 'Huvitavad faktid', en: 'Fun facts' },
    personal_id_code: { et: 'Isikukood', en: 'Personal ID code' },
    dietary_preference: { et: 'Dieedivalik', en: 'Dietary preference' },
    allergies: { et: 'Allergiad', en: 'Allergies' },
    prefer_not_to_eat: { et: 'Ei soovi süüa', en: 'Prefer not to eat' },
    bank_name: { et: 'Panga nimi', en: 'Bank name' },
    bank_account_holder: { et: 'Konto omanik', en: 'Account holder' },
    iban: { et: 'IBAN', en: 'IBAN' },
    swift: { et: 'SWIFT', en: 'SWIFT' },
    emergency_contacts: { et: 'Hädaabikontaktid', en: 'Emergency contacts' },
    experiences: { et: 'Kogemused', en: 'Experiences' },
    employment_start_date: { et: 'Tööle asumise kuupäev', en: 'Employment start date' },
    probation_end_date: { et: 'Katseaja lõpp', en: 'Probation end date' },
    employee_id: { et: 'Töötaja ID', en: 'Employee ID' },
    contract_type: { et: 'Lepingu tüüp', en: 'Contract type' },
    place_of_work: { et: 'Töökoht', en: 'Place of work' },
    teams: { et: 'Tiimid', en: 'Teams' },
    contract_date: { et: 'Lepingu kuupäev', en: 'Contract date' },
    job_title: { et: 'Ametikoht', en: 'Job title' },
    role_details: { et: 'Rolli kirjeldus', en: 'Role details' },
    working_hours_per_week: { et: 'Töötunnid nädalas', en: 'Working hours per week' },
    salary: { et: 'Palk', en: 'Salary' },
    bonus: { et: 'Boonus', en: 'Bonus' },
    sport_benefit: { et: 'Sporditoetus', en: 'Sport benefit' },
    health_benefit: { et: 'Tervisetoetus', en: 'Health benefit' },
    employment_end_date: { et: 'Töösuhte lõpp', en: 'Employment end date' },
    leaving_reason: { et: 'Lahkumise põhjus', en: 'Leaving reason' },
    leaving_file: { et: 'Lahkumisavalduse link', en: 'Resignation letter link' },
    leaving_note: { et: 'Lahkumise märkus', en: 'Leaving note' },
    turnover_impact: { et: 'Lahkumismõju', en: 'Turnover impact' },
}
